<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";

  .tab {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;

    // Allows the table to extend the full height of the modal
    // so that the empty state is vertically centred
    .right {
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: auto; // required for the table alone to scroll
    }

    &.skeleton {
      overflow: hidden;
    }

    &.compact {
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
      .left {
        padding-right: utils.in_rem(30px);
        min-width: 35%; // expand the cards to fill the space
      }
      .right {
        min-width: 65%;
      }
    }
    &.normal {
      flex-direction: column;
    }

    .tab-heading {
      display: flex;
      align-items: center;
      padding: utils.in_rem(10px) utils.in_rem(2px) utils.in_rem(20px) 0;
      gap: 4px;

      .campaign-overview {
        @include mixins.h4();
        @include colors.color(black_90);

        .campaign-link {
          margin-left: 0.5rem;
          @include colors.color(primary_50);
          &:hover {
            @include colors.color(primary_70);
          }
        }
      }
    }

    .tab-stat-cards {
      display: flex;
      justify-content: space-between;
      gap: utils.in_rem(12px);

      // In the contact modal, there is no campaign header, so we add padding to the body
      &.addPadding {
        padding-top: utils.in_rem(20px);
      }

      &.compact {
        flex-direction: column;
      }

      .caption {
        @include mixins.caption();
        @include colors.color(textGrey);
      }
    }

    .tab-history {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: utils.in_rem(16px) 2px utils.in_rem(20px) 0;
    }
  }
</style>

<script>
  // Components
  import ActivityIcon from "@sourcewhale/shared/svelte-design-system/components/atoms/ActivityIcon.svelte";
  import EmailEngagementTable from "../components/EmailEngagementTable.svelte";
  import StatCard from "../components/StatCard.svelte";
  import TableSelectDropdown from "../components/TableSelectDropdown.svelte";
  // Stores
  import {
    clicksTrackingStore,
    compactModal,
    opensTrackingStore,
    selectedTableStore,
    todosModalItemStore,
  } from "../stores";

  const opensTrackingStoreMeta = opensTrackingStore.meta;
  const clicksTrackingStoreMeta = clicksTrackingStore.meta;

  const items = [
    { text: "Clicks", value: "clicks" },
    { text: "Opens", value: "opens" },
  ];
  // Set default selected table as the first element in the items array
  $: $selectedTableStore.emailEngagement ??= items[0];
  $: compactModalClass = $compactModal ? "compact" : "normal";

  $: opensSkeleton =
    $opensTrackingStoreMeta.firstFetchDone && !$opensTrackingStoreMeta.fetching
      ? ""
      : "skeleton";
  $: clicksSkeleton =
    $clicksTrackingStoreMeta.firstFetchDone &&
    !$clicksTrackingStoreMeta.fetching
      ? ""
      : "skeleton";

  $: isTodo = $todosModalItemStore?.toDoId;
  $: href = $todosModalItemStore?.campaignId
    ? `/campaign?campaignId=${$todosModalItemStore.campaignId}`
    : "";
</script>

<div class="tab {compactModalClass} {opensSkeleton} {clicksSkeleton}">
  <!-- Left and right used for compact modal -->
  <div class="left">
    {#if isTodo}
      <!-- We show all campaigns in the non-todos contact modal -->
      <div class="tab-heading">
        <span class="campaign-overview">
          Overview:<a
            {href}
            class="campaign-link"
            target="_blank"
            rel="noopener noreferrer">{$todosModalItemStore?.campaignName}</a
          >
        </span>
      </div>
    {/if}
    <div class="tab-stat-cards {compactModalClass}" class:addPadding={!isTodo}>
      <StatCard class="flex-1 {opensSkeleton}">
        <span class={opensSkeleton}
          >{opensSkeleton ? "Loading..." : $opensTrackingStore.length}</span
        >
        <ActivityIcon
          slot="icon"
          size="medium"
          kind="opens"
          skeleton={Boolean(opensSkeleton)}
        />
        <span class="caption {opensSkeleton}" slot="data">Opens</span>
      </StatCard>
      <StatCard class="flex-1 {clicksSkeleton}">
        <span class={clicksSkeleton}
          >{clicksSkeleton ? "Loading..." : $clicksTrackingStore.length}</span
        >
        <ActivityIcon
          slot="icon"
          size="medium"
          kind="clicks"
          skeleton={Boolean(clicksSkeleton)}
        />
        <span class="caption {clicksSkeleton}" slot="data">Clicks</span>
      </StatCard>
    </div>
  </div>
  <div class="right">
    <div class="tab-history">
      <h3>History</h3>
      <TableSelectDropdown
        {items}
        bind:selectedItem={$selectedTableStore.emailEngagement}
      />
    </div>
    <EmailEngagementTable
      tableType={$selectedTableStore.emailEngagement.value}
    />
  </div>
</div>
